import type { Empresa } from "@/componentes/empresa/tipos"
import { pieDeCompania } from "@/componentes/identidad/identidad"
import { MarcaCompania } from "@/componentes/impresos/MarcaCompania"
import { Ondas } from "@/componentes/impresos/Ondas"
import { dato, filete, titular, versal } from "./estilo"
import { ALTO_HOJA, MEDIDAS } from "./medidas"
import type { DatosCaratula } from "./tipos"

/** Hasta dónde llega el contenido: debajo sólo quedan las ondas (manual: 820 de 1056). */
const ALTO_DEL_TEXTO = 0.7765

/**
 * La carátula o separador. Ocupa la HOJA entera, a sangre, y se imprime sobre papel de
 * color: por eso no tiene un solo fondo blanco.
 *
 * ⚠ **`papel` sólo simula la cartulina en pantalla.** El pliego no lo pasa: imprimir un
 * color de fondo sobre un papel que ya lo tiene gastaría la tinta que el manual ahorra.
 */
export function CaratulaCarpeta({
  datos,
  empresa,
  papel,
}: {
  datos: DatosCaratula
  empresa?: Empresa
  papel?: string
}) {
  const hoja = datos.hoja ?? "CARTA"
  const { ancho } = MEDIDAS.CARATULA
  const alto = ALTO_HOJA[hoja]
  const largo = datos.titulo.length
  const cuerpoTitulo = largo > 70 ? 9.5 : largo > 40 ? 12 : 16.4
  const pies = [
    ["Sección", datos.seccion],
    ["Responsable", datos.responsable],
    ["Ubicación", datos.ubicacion],
  ].filter((fila): fila is [string, string] => Boolean(fila[1]))

  return (
    <article
      className="pieza-impresa"
      style={{
        position: "relative",
        width: `${ancho}mm`,
        height: `${alto}mm`,
        padding: "19mm 19mm 0",
        fontFamily: "var(--font-rotulo-texto)",
        background: papel,
      }}
      aria-label={`Carátula: ${datos.titulo || "sin título"}`}
    >
      <Ondas pieza="caratula" style={{ inset: 0, width: "100%", height: "100%" }} />

      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          height: `${alto * ALTO_DEL_TEXTO}mm`,
        }}
      >
        <header style={{ display: "flex", justifyContent: "space-between", gap: "8mm" }}>
          <MarcaCompania compania={datos.compania} alto={24.4} />
          {datos.codigo ? (
            <div style={{ textAlign: "right" }}>
              <p style={versal(2.9, { color: "var(--impreso-texto-2)" })}>Expediente</p>
              <p
                className="tabular"
                style={titular(9.5, { marginTop: "1.6mm", letterSpacing: "0.02em" })}
              >
                {datos.codigo}
              </p>
            </div>
          ) : null}
        </header>

        <div style={filete({ marginTop: "10.6mm" })} />

        <main style={{ display: "grid", gap: "3.7mm", marginTop: "30mm" }}>
          {datos.entidad ? (
            <p style={dato(4.2, { color: "var(--impreso-texto-3)" })}>
              {datos.entidad}
              {datos.rif ? (
                <span className="tabular" style={{ color: "var(--impreso-texto-2)" }}>
                  {" · RIF "}
                  {datos.rif}
                </span>
              ) : null}
            </p>
          ) : null}
          {datos.departamento ? (
            <p style={versal(4, { letterSpacing: "0.26em" })}>{datos.departamento}</p>
          ) : null}
          <h1 style={titular(cuerpoTitulo, { letterSpacing: "-0.025em", textWrap: "balance" })}>
            {datos.titulo}
          </h1>
          {datos.subtitulo ? (
            <p style={dato(5, { color: "var(--impreso-texto-3)" })}>{datos.subtitulo}</p>
          ) : null}
          {datos.periodo ? (
            <p style={dato(5.8, { color: "var(--impreso-texto-3)" })}>{datos.periodo}</p>
          ) : null}
        </main>

        {pies.length > 0 ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: "7.4mm",
              marginTop: "auto",
            }}
          >
            {pies.map(([nombre, valor]) => (
              <div
                key={nombre}
                style={{
                  display: "grid",
                  gap: "2.6mm",
                  paddingTop: "3.2mm",
                  borderTop: "0.26mm solid var(--impreso-noche)",
                }}
              >
                <p style={versal(2.9, { letterSpacing: "0.2em", color: "var(--impreso-texto-2)" })}>
                  {nombre}
                </p>
                <p style={dato(4.2)}>{valor}</p>
              </div>
            ))}
          </div>
        ) : null}

        {datos.con_membrete !== false ? (
          <footer
            style={dato(3.4, {
              fontWeight: 400,
              color: "var(--impreso-texto-2)",
              marginTop: pies.length > 0 ? "9.5mm" : "auto",
            })}
          >
            {pieDeCompania(datos.compania ?? "SETIECA_AGENTES", empresa)}
          </footer>
        ) : null}
      </div>
    </article>
  )
}
